import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft,
  ChevronRight,
  Clock,
  MessageCircle,
  CheckCircle2,
  AlertTriangle,
  Info,
  Bell,
  Calendar,
  Gift,
  Camera,
  Link as LinkIcon,
  ShieldCheck,
} from "lucide-react";

function TutorialStep({ number, title, children }) {
  return (
    <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden text-left [&_p]:!text-justify [&_li]:!text-justify">
      <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
      <h3 className="text-xl font-bold text-gray-900 mt-0 mb-4 flex items-center gap-3">
        <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black shrink-0">
          {number}
        </span>
        {title}
      </h3>
      <div className="text-gray-600">{children}</div>
    </div>
  );
}

export default function WhatsAppBusinessTutorialPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 font-sans pb-20">
      <header className="border-b bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/">
            <img
              src="/marketing/logo_official.png"
              className="h-28 object-contain"
              alt="OdontoAssist AI Logo"
            />
          </Link>

          <div className="flex items-center gap-6">
            <Link
              to="/ajuda/tutoriais"
              className="text-gray-500 hover:text-indigo-600 font-semibold hidden md:block"
            >
              Central de Guias
            </Link>

            <Link
              to="/"
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl font-black tracking-wide flex items-center gap-2 transition-all shadow-lg hover:-translate-y-1"
            >
              <ArrowLeft size={20} />
              Voltar {user ? "para o Aplicativo" : "ao Início"}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 mb-6">
          <Link to="/ajuda" className="hover:text-indigo-600">Ajuda</Link>
          <ChevronRight size={14} className="text-gray-400" />
          <Link to="/ajuda/tutoriais" className="hover:text-indigo-600">Guias</Link>
          <ChevronRight size={14} className="text-gray-400" />
          <span className="text-indigo-600">WhatsApp Business Integrado</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight">
          WhatsApp Business Integrado
        </h1>

        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-8">
          <div className="flex items-center gap-2">
            <Clock size={16} /> <span>12 min de leitura</span>
          </div>
          <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1 rounded-full font-bold uppercase tracking-wider text-xs">
            Módulo WhatsApp
          </div>
        </div>

        <article className="prose prose-indigo prose-lg max-w-none text-gray-600 text-left [&_p]:!text-justify [&_li]:!text-justify">
          <p className="lead text-xl text-gray-700 font-medium">
            O Módulo WhatsApp Business Integrado permite que a clínica envie mensagens automáticas
            aos pacientes e acompanhe respostas diretamente dentro do OdontoAssist AI, utilizando
            a API Oficial do WhatsApp Business da Meta.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-3xl p-6 my-8">
            <h4 className="text-amber-900 font-bold flex items-center gap-2 mt-0">
              <AlertTriangle size={22} />
              Importante: este módulo não é a Extensão WhatsApp Web
            </h4>
            <p className="mb-0 text-amber-800">
              O <strong>WhatsApp Business Integrado</strong> funciona pela API oficial da Meta e envia mensagens
              diretamente pelo sistema. Já a <strong>Extensão WhatsApp Web</strong> é uma ferramenta separada,
              usada no navegador, para apoiar o atendimento manual pelo WhatsApp Web comum.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Passo a Passo em Texto</h2>

          <div className="space-y-8 mt-8">
          <TutorialStep number="1" title="O que o módulo permite fazer">

          <ul className="list-none p-0 m-0 grid md:grid-cols-2 gap-x-8 gap-y-4">
            {[
              ["Confirmações de consulta", Calendar],
              ["Lembretes de agendamento", Bell],
              ["Remarcações e cancelamentos", CheckCircle2],
              ["Mensagens de aniversário", Gift],
              ["Link de autoagendamento", LinkIcon],
              ["Link para envio da foto do paciente", Camera],
              ["Central WhatsApp Business", MessageCircle],
              ["Status enviada, entregue e lida", ShieldCheck],
            ].map(([title, Icon]) => (
              <li key={title} className="m-0 p-0 flex items-start gap-3">
                <Icon className="w-5 h-5 text-green-600 shrink-0 mt-1" />
                <span className="font-semibold text-gray-800">{title}</span>
              </li>
            ))}
          </ul>
          </TutorialStep>

          <TutorialStep number="2" title="Como contratar o módulo">

          <p>
            A contratação é feita em <strong>Configurações → Módulo WhatsApp Business</strong>.
            O módulo possui cobrança própria, separada da mensalidade principal do OdontoAssist AI.
          </p>

          <p>
            Após a contratação, o sistema libera os recursos de WhatsApp para a clínica, respeitando
            o limite mensal contratado. Caso existam mensagens excedentes, elas podem ser cobradas
            conforme a regra comercial vigente do módulo.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-3xl p-6 my-8">
            <h4 className="text-blue-900 font-bold flex items-center gap-2 mt-0">
              <Info size={22} />
              Dica
            </h4>
            <p className="mb-0 text-blue-800">
              Antes de contratar, confira se os dados de cobrança da assinatura principal estão completos.
              Em alguns casos, o sistema pode solicitar CPF/CNPJ e telefone do pagador.
            </p>
          </div>
          </TutorialStep>

          <TutorialStep number="3" title="Configurações essenciais">

          <p>
            Para que as mensagens saiam completas e profissionais, mantenha atualizados em
            <strong> Configurações → Dados Básicos</strong>:
          </p>

          <ul>
            <li><strong>Nome da clínica</strong> — aparece no corpo das mensagens.</li>
            <li><strong>WhatsApp da clínica</strong> — indicado para o paciente retornar contato diretamente com a clínica.</li>
            <li><strong>Telefone da clínica</strong> — usado como contato alternativo.</li>
            <li><strong>Endereço</strong> — usado em mensagens de confirmação de consulta.</li>
          </ul>
          </TutorialStep>

          <TutorialStep number="4" title="Mensagens automáticas disponíveis">
          <ul className="list-none p-0 m-0 space-y-5">
            <li className="m-0 p-0 flex items-start gap-3">
              <Calendar className="shrink-0 text-indigo-500 mt-1" size={20} />
              <div><strong>Confirmação de consulta:</strong><br />Pode ser enviada manualmente pela agenda ou automaticamente conforme a configuração da clínica. A mensagem informa data, horário, profissional, clínica, endereço e contatos.</div>
            </li>
            <li className="m-0 p-0 flex items-start gap-3">
              <Bell className="shrink-0 text-indigo-500 mt-1" size={20} />
              <div><strong>Lembrete de consulta:</strong><br />Ajuda a reduzir faltas e esquecimentos. Pode ser configurado para lembrar o paciente antes da consulta.</div>
            </li>
            <li className="m-0 p-0 flex items-start gap-3">
              <CheckCircle2 className="shrink-0 text-indigo-500 mt-1" size={20} />
              <div><strong>Remarcação e cancelamento:</strong><br />Quando a consulta é remarcada ou cancelada, a clínica pode enviar uma mensagem padronizada informando a alteração ao paciente.</div>
            </li>
            <li className="m-0 p-0 flex items-start gap-3">
              <Gift className="shrink-0 text-indigo-500 mt-1" size={20} />
              <div><strong>Aniversário:</strong><br />Na página <strong>Lembretes</strong>, os aniversários aparecem automaticamente quando o paciente possui data de nascimento cadastrada. Para pacientes com telefone cadastrado, a clínica pode usar o botão <strong>Enviar WhatsApp</strong> para enviar uma mensagem de felicitação.</div>
            </li>
            <li className="m-0 p-0 flex items-start gap-3">
              <LinkIcon className="shrink-0 text-indigo-500 mt-1" size={20} />
              <div><strong>Link de autoagendamento:</strong><br />Pelo prontuário do paciente, a clínica pode enviar o link de autoagendamento por WhatsApp. Assim o paciente escolhe profissional, data e horário disponíveis.</div>
            </li>
            <li className="m-0 p-0 flex items-start gap-3">
              <Camera className="shrink-0 text-indigo-500 mt-1" size={20} />
              <div><strong>Link para envio da foto do paciente:</strong><br />Também pelo prontuário, é possível enviar um link para que o paciente tire ou envie sua foto. O link expira em 7 dias.</div>
            </li>
          </ul>
          </TutorialStep>

          <TutorialStep number="5" title="Central WhatsApp Business">

          <p>
            A página <strong>WhatsApp Business</strong> reúne as conversas relacionadas às mensagens enviadas
            pelo OdontoAssist AI. Quando o paciente responde uma mensagem do sistema, a conversa aparece
            automaticamente na Central.
          </p>

          <p>Na Central, você pode:</p>

          <ul>
            <li>ver conversas abertas com pacientes;</li>
            <li>acompanhar mensagens enviadas e recebidas;</li>
            <li>identificar mensagens não lidas;</li>
            <li>responder pacientes durante a janela de atendimento;</li>
            <li>visualizar o status das mensagens enviadas.</li>
          </ul>
          </TutorialStep>

          <TutorialStep number="6" title="Entendendo os status">

          <ul>
            <li><strong>Enviada:</strong> o OdontoAssist enviou a mensagem para a Meta.</li>
            <li><strong>Entregue:</strong> o WhatsApp confirmou a entrega ao aparelho/conta do paciente.</li>
            <li><strong>Lida:</strong> o paciente abriu ou visualizou a mensagem, quando essa informação estiver disponível.</li>
            <li><strong>Falhou:</strong> a mensagem não pôde ser entregue.</li>
          </ul>
          </TutorialStep>

          <TutorialStep number="7" title="A janela de atendimento de 24 horas">

          <div className="bg-green-50 border border-green-200 rounded-3xl p-6 my-8">
            <h4 className="text-green-900 font-bold flex items-center gap-2 mt-0">
              <MessageCircle size={22} />
              Como funciona
            </h4>
            <p className="text-green-800">
              Quando o paciente responde uma mensagem enviada pela clínica, o WhatsApp abre uma
              <strong> janela de atendimento de 24 horas</strong>. Durante esse período, a equipe pode
              responder livremente pela Central WhatsApp Business.
            </p>
            <p className="text-green-800 mb-0">
              Após 24 horas sem nova mensagem do paciente, essa janela é encerrada automaticamente.
            </p>
          </div>

          <p>Exemplo prático:</p>

          <ol className="list-none p-0 space-y-3">
            <li className="m-0 p-0 flex items-start gap-3"><span className="w-7 h-7 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-black shrink-0">1</span><span>A clínica envia uma confirmação de consulta.</span></li>
            <li className="m-0 p-0 flex items-start gap-3"><span className="w-7 h-7 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-black shrink-0">2</span><span>O paciente responde: “Ok, obrigado”.</span></li>
            <li className="m-0 p-0 flex items-start gap-3"><span className="w-7 h-7 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-black shrink-0">3</span><span>A conversa aparece na Central WhatsApp Business.</span></li>
            <li className="m-0 p-0 flex items-start gap-3"><span className="w-7 h-7 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-black shrink-0">4</span><span>A clínica pode responder livremente por até 24 horas.</span></li>
            <li className="m-0 p-0 flex items-start gap-3"><span className="w-7 h-7 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-black shrink-0">5</span><span>Depois desse prazo, a janela é encerrada.</span></li>
          </ol>

          <div className="bg-amber-50 border border-amber-200 rounded-3xl p-6 my-8">
            <h4 className="text-amber-900 font-bold flex items-center gap-2 mt-0">
              <AlertTriangle size={22} />
              Por que não consigo responder?
            </h4>
            <p className="mb-0 text-amber-800">
              Quando aparecer <strong>Janela de atendimento encerrada</strong>, significa que passaram mais
              de 24 horas desde a última mensagem enviada pelo paciente. Isso não é uma falha do OdontoAssist AI:
              é uma regra da API Oficial do WhatsApp Business da Meta.
            </p>
          </div>
          </TutorialStep>

          <TutorialStep number="8" title="Diferença entre Central WhatsApp e WhatsApp Web">

          <p>
            A <strong>Central WhatsApp Business</strong> mostra as conversas vinculadas ao número oficial da
            Meta usado pelo módulo. Ela é ideal para acompanhar respostas a mensagens automáticas e atender
            pacientes dentro da janela de 24 horas.
          </p>

          <p>
            A <strong>Extensão WhatsApp Web</strong>, por outro lado, é usada quando a clínica quer abrir o
            WhatsApp Web tradicional no navegador e trabalhar com o número próprio da clínica, fora da API oficial.
          </p>
          </TutorialStep>

          <TutorialStep number="9" title="Perguntas frequentes">

          <h4 className="font-bold text-gray-900 mt-5 mb-1">O paciente precisa instalar algo?</h4>
          <p>Não. Ele recebe a mensagem normalmente no WhatsApp dele.</p>

          <h4 className="font-bold text-gray-900 mt-5 mb-1">Posso conversar livremente com qualquer paciente?</h4>
          <p>
            Não. Mensagens livres só podem ser enviadas quando o paciente enviou uma mensagem nos últimos 24 horas.
          </p>

          <h4 className="font-bold text-gray-900 mt-5 mb-1">O que acontece se o paciente responder?</h4>
          <p>
            A conversa aparece na Central WhatsApp Business vinculada à clínica correta, sempre que o sistema
            conseguir identificar o vínculo pelo histórico da mensagem ou pelo telefone cadastrado.
          </p>

          <h4 className="font-bold text-gray-900 mt-5 mb-1">O que acontece com mensagens de números desconhecidos?</h4>
          <p>
            Por segurança, mensagens de números não identificados não são entregues automaticamente a uma clínica.
            Isso evita que uma mensagem seja exibida para o usuário errado.
          </p>

          <h4 className="font-bold text-gray-900 mt-5 mb-1">Posso enviar promoções?</h4>
          <p>
            Mensagens promocionais podem ser classificadas como Marketing pela Meta e ter cobrança diferente.
            O módulo padrão prioriza comunicações clínicas e operacionais.
          </p>

          <h4 className="font-bold text-gray-900 mt-5 mb-1">Dois pacientes podem ter o mesmo telefone?</h4>
          <p>
            Sim, como mãe e filho ou responsável e representado. Quando possível, o OdontoAssist usa o histórico
            da mensagem para identificar o paciente correto. Em situações ambíguas, o sistema evita vincular
            automaticamente para não associar a conversa ao paciente errado.
          </p>
          </TutorialStep>

          <TutorialStep number="10" title="Boas práticas">

          <ul>
            <li>Mantenha o telefone e WhatsApp da clínica sempre atualizados.</li>
            <li>Cadastre corretamente o telefone dos pacientes.</li>
            <li>Consulte a Central WhatsApp diariamente.</li>
            <li>Evite usar mensagens promocionais sem entender a categoria e custo envolvidos.</li>
            <li>Oriente a equipe sobre a janela de atendimento de 24 horas.</li>
          </ul>
          </TutorialStep>
          </div>
        </article>
      </main>
    </div>
  );
}
